import { useTranslation } from 'react-i18next';
import type { CustomField } from '@/common/api/types/customField';
import type {
  ItemCheckboxValue,
  ItemCurrencyValue,
  ItemCustomFieldValue,
  ItemDateValue,
  ItemItemValue,
  ItemLinkValue,
  ItemMultiCustomerValue,
  ItemMultiItemValue,
  ItemMultiLineTextValue,
  ItemMultiLinkValue,
  ItemMultiPicklistValue,
  ItemMultiTextValue,
  ItemMultiUserValue,
  ItemNoteValue,
  ItemNumericValue,
  ItemPicklistValue,
  ItemTextValue,
  ItemUserValue,
} from '@/common/api/types/items';
import type { ItemTypeMap } from '@/common/api/types/itemType';
import { FieldDisplayContext } from '@/features/items/components/item-field-value/FieldDisplayContext';
import { CheckboxField } from './CheckboxField';
import { CurrencyField } from './CurrencyField';
import { DateField } from './DateField';
import { ItemField } from './ItemField';
import { LinkField } from './LinkField';
import { MultiCustomerField } from './MultiCustomerField';
import { MultiItemField } from './MultiItemField';
import { type LinkValue, MultiLinkField } from './MultiLinkField';
import { MultilineTextField } from './MultilineTextField';
import { MultiPicklistField } from './MultiPicklistField';
import { MultiTextField } from './MultiTextField';
import { MultiUserField } from './MultiUserField';
import { NoteField } from './NoteField';
import { NumericField } from './NumericField';
import { PicklistField } from './PicklistField';
import { TextField } from './TextField';
import { UserField } from './UserField';

interface CustomFieldValueProps {
  customField: CustomField;
  itemTypesById: ItemTypeMap;
  value?: ItemCustomFieldValue;
  isEditable?: boolean;
  onChange: (newValue: unknown) => void;
  isPending?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  error?: Error | null;
  hideLabel?: boolean;
  autoFocus?: boolean;
}

export const CustomFieldValue = ({
  customField,
  itemTypesById,
  value,
  isEditable = false,
  onChange,
  isPending,
  isSuccess,
  isError,
  error,
  hideLabel = false,
  autoFocus = false,
}: CustomFieldValueProps) => {
  const { t } = useTranslation();
  const { id: fieldId, type: fieldType, name: rawFieldName, archivedAt } = customField;
  const fieldName =
    archivedAt != null ? `${rawFieldName} (${t(($) => $.common.archived)})` : rawFieldName;

  const field = (() => {
    switch (fieldType) {
      case 'CHECKBOX':
        return (
          <CheckboxField
            id={`${fieldId}`}
            name={fieldName}
            value={(value as ItemCheckboxValue | undefined)?.CHECKBOX}
            isEditable={isEditable}
            onChange={onChange as (value: boolean) => void}
            isPending={isPending}
            isSuccess={isSuccess}
            isError={isError}
            error={error}
          />
        );
      case 'CURRENCY':
        return (
          <CurrencyField
            id={`${fieldId}`}
            name={fieldName}
            value={(value as ItemCurrencyValue | undefined)?.CURRENCY}
            currencyUnit={customField.currencyUnit ?? null}
            isEditable={isEditable}
            onChange={onChange as (value: number | undefined) => void}
            isPending={isPending}
            isSuccess={isSuccess}
            isError={isError}
            error={error}
            autoFocus={autoFocus}
          />
        );
      case 'DATE':
        return (
          <DateField
            id={`${fieldId}`}
            name={fieldName}
            value={(value as ItemDateValue | undefined)?.DATE}
            isEditable={isEditable}
            autoFocus={autoFocus}
            onChange={onChange as (value: string | null) => void}
            isPending={isPending}
            isSuccess={isSuccess}
            isError={isError}
            error={error}
          />
        );
      case 'ITEM':
        return (
          <ItemField
            id={`${fieldId}`}
            name={fieldName}
            value={value as ItemItemValue | undefined}
            itemTypesById={itemTypesById}
            isEditable={isEditable}
            autoFocus={autoFocus}
            onChange={onChange as (value: number | null) => void}
            isPending={isPending}
            isSuccess={isSuccess}
            isError={isError}
            error={error}
          />
        );
      case 'LINK': {
        const rawLinkValue = value as ItemLinkValue | undefined;
        const link: unknown = rawLinkValue?.LINK;
        const normalizedLinkValue: ItemLinkValue | undefined =
          rawLinkValue == null
            ? undefined
            : typeof link === 'object' && link !== null && 'url' in link
              ? {
                  LINK: String((link as { url?: unknown }).url ?? ''),
                  description:
                    (link as { description?: string }).description ?? rawLinkValue.description,
                }
              : rawLinkValue;
        return (
          <LinkField
            id={`${fieldId}`}
            name={fieldName}
            value={normalizedLinkValue}
            isEditable={isEditable}
            autoFocus={autoFocus}
            onChange={onChange as (value: ItemLinkValue | undefined) => void}
            isPending={isPending}
            isSuccess={isSuccess}
            isError={isError}
            error={error}
          />
        );
      }
      case 'MULTI_LINK':
        return (
          <MultiLinkField
            id={`${fieldId}`}
            name={fieldName}
            values={value as ItemMultiLinkValue[] | undefined}
            isEditable={isEditable}
            autoFocus={autoFocus}
            isPending={isPending}
            isSuccess={isSuccess}
            isError={isError}
            error={error}
            onChange={onChange as (links: LinkValue[]) => void}
          />
        );
      case 'MULTILINE_TEXT':
        return (
          <MultilineTextField
            id={`${fieldId}`}
            name={fieldName}
            value={(value as ItemMultiLineTextValue | undefined)?.MULTILINE_TEXT}
            isEditable={isEditable}
            onChange={onChange as (value: string) => void}
            isPending={isPending}
            isSuccess={isSuccess}
            isError={isError}
            error={error}
            autoFocus={autoFocus}
          />
        );
      case 'MULTI_ITEM':
        return (
          <MultiItemField
            id={`${fieldId}`}
            name={fieldName}
            values={value as ItemMultiItemValue[] | undefined}
            itemTypesById={itemTypesById}
            isEditable={isEditable}
            autoFocus={autoFocus}
            isPending={isPending}
            isSuccess={isSuccess}
            isError={isError}
            error={error}
            onChange={onChange as (ids: number[]) => void}
          />
        );
      case 'MULTI_PICKLIST':
        return (
          <MultiPicklistField
            id={`${fieldId}`}
            name={fieldName}
            values={value as ItemMultiPicklistValue[] | undefined}
            isEditable={isEditable}
            autoFocus={autoFocus}
            isPending={isPending}
            isSuccess={isSuccess}
            isError={isError}
            error={error}
            onChange={onChange as (ids: number[]) => void}
            options={customField.picklistOptions}
          />
        );
      case 'MULTI_TEXT':
        return (
          <MultiTextField
            id={`${fieldId}`}
            name={fieldName}
            values={value as ItemMultiTextValue[] | undefined}
            isEditable={isEditable}
            autoFocus={autoFocus}
            isPending={isPending}
            isSuccess={isSuccess}
            isError={isError}
            error={error}
            onChange={onChange as (values: string[]) => void}
          />
        );
      case 'MULTI_USER':
        return (
          <MultiUserField
            name={fieldName}
            values={value as ItemMultiUserValue[] | undefined}
            isEditable={isEditable}
            autoFocus={autoFocus}
            isPending={isPending}
            isSuccess={isSuccess}
            isError={isError}
            error={error}
            onChange={onChange as (userIds: string[]) => void}
          />
        );
      case 'MULTI_CUSTOMER':
        return (
          <MultiCustomerField
            name={fieldName}
            values={value as ItemMultiCustomerValue[] | undefined}
            isEditable={isEditable}
            autoFocus={autoFocus}
            isPending={isPending}
            isSuccess={isSuccess}
            isError={isError}
            error={error}
            onChange={onChange as (customerIds: number[]) => void}
          />
        );
      case 'NOTE':
        return (
          <NoteField
            name={fieldName}
            value={(value as ItemNoteValue | undefined)?.NOTE}
            isEditable={isEditable}
            onChange={onChange as (value: string) => void}
            isPending={isPending}
            isSuccess={isSuccess}
            isError={isError}
            error={error}
          />
        );
      case 'NUMERIC':
        return (
          <NumericField
            id={`${fieldId}`}
            name={fieldName}
            value={(value as ItemNumericValue | undefined)?.NUMERIC}
            isEditable={isEditable}
            onChange={onChange as (value: number | undefined) => void}
            isPending={isPending}
            isSuccess={isSuccess}
            isError={isError}
            error={error}
            autoFocus={autoFocus}
          />
        );
      case 'PICKLIST':
        return (
          <PicklistField
            id={`${fieldId}`}
            name={fieldName}
            value={value as ItemPicklistValue | undefined}
            isEditable={isEditable}
            onChange={onChange as (value: number | null) => void}
            isPending={isPending}
            isSuccess={isSuccess}
            isError={isError}
            error={error}
            autoFocus={autoFocus}
            options={customField.picklistOptions}
          />
        );
      case 'TEXT':
        return (
          <TextField
            id={`${fieldId}`}
            name={fieldName}
            value={(value as ItemTextValue | undefined)?.TEXT}
            isEditable={isEditable}
            onChange={onChange as (value: string) => void}
            isPending={isPending}
            isSuccess={isSuccess}
            isError={isError}
            error={error}
            autoFocus={autoFocus}
          />
        );
      case 'USER':
        return (
          <UserField
            id={`${fieldId}`}
            name={fieldName}
            value={value as ItemUserValue | undefined}
            isEditable={isEditable}
            autoFocus={autoFocus}
            onChange={onChange as (value: string | undefined) => void}
            isPending={isPending}
            isSuccess={isSuccess}
            isError={isError}
            error={error?.message}
          />
        );
      default:
        return null;
    }
  })();

  return <FieldDisplayContext.Provider value={{ hideLabel }}>{field}</FieldDisplayContext.Provider>;
};
